import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ReconcilableAccount,
  ReconciliationSummary,
} from '../clients/rust-api-client';
import { ledgerService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import { Subheading } from '../common/Catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../common/Catalyst/table';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
  sameDecimal,
} from '../utils/billingHelpers';
import OpenReconciliationDialog from './OpenReconciliationDialog';

interface Props {
  hoaId: string;
  canReconcile: boolean;
}

/**
 * Every reconciliation, plus every cash account that has none.
 *
 * The second half is the point. A list of what has been reconciled
 * cannot answer *"is this month done?"* — only a list that also names
 * the accounts nobody has started can, and working out which GL
 * accounts those are is a rule the backend already owns
 * (`require_cash_account`). A second copy of it here would drift.
 */
const ReconciliationList: React.FC<Props> = ({ hoaId, canReconcile }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ReconciliationSummary[]>([]);
  const [unstarted, setUnstarted] = useState<ReconcilableAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState<ReconcilableAccount | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await ledgerService.listReconciliations({ hoaId });
      setRows(data.reconciliations);
      setUnstarted(data.unreconciled_accounts);
    } catch (err) {
      console.error('Error loading reconciliations:', err);
      setError(billingErrorMessage(err, "Couldn't load the reconciliations."));
    } finally {
      setLoading(false);
    }
  }, [hoaId]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = (recId: string) =>
    navigate(`/hoa/${hoaId}/accounting/reconciliations/${recId}`);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load the reconciliations"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline={true}
          />
        </div>
      )}

      <Subheading>Reconciliations</Subheading>
      {rows.length === 0 ? (
        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
          No account has been reconciled yet.
        </p>
      ) : (
        <Table dense className="mt-3">
          <TableHead>
            <TableRow>
              <TableHeader>Account</TableHeader>
              <TableHeader>Statement</TableHeader>
              <TableHeader className="text-right">
                Statement balance
              </TableHeader>
              <TableHeader className="text-right">Difference</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader> </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  {r.account_number} {r.account_name}
                </TableCell>
                <TableCell>{formatBillingDate(r.statement_date)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatMoney(r.statement_balance)}
                </TableCell>
                {/*
                  The only number that matters. Compared as a decimal
                  string rather than parsed: `0.00`, `-0.00` and `0` are
                  the same reconciled account, and parsing money into a
                  float is how a screen stops agreeing with the books.
                */}
                <TableCell
                  className={
                    sameDecimal(r.difference, '0')
                      ? 'text-right tabular-nums text-green-600 dark:text-green-400'
                      : 'text-right tabular-nums text-red-600 dark:text-red-400 font-semibold'
                  }
                >
                  {formatMoney(r.difference)}
                </TableCell>
                <TableCell>
                  {r.status === 'completed' ? (
                    <Badge color="green">Completed</Badge>
                  ) : (
                    <Badge color="amber">In progress</Badge>
                  )}
                  {r.proposed_count > 0 && (
                    <Badge color="amber" className="ml-1">
                      {r.proposed_count} proposed
                    </Badge>
                  )}
                  {r.unexplained_count > 0 && (
                    <Badge color="zinc" className="ml-1">
                      {r.unexplained_count} unexplained
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Button plain onClick={() => open(r.id)}>
                    {r.status === 'completed' ? 'View' : 'Reconcile'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {unstarted.length > 0 && (
        <div className="mt-8">
          <Subheading>Not started</Subheading>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            Cash and investment accounts with no reconciliation in this list.
          </p>
          <Table dense className="mt-3">
            <TableHead>
              <TableRow>
                <TableHeader>Account</TableHeader>
                <TableHeader> </TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {unstarted.map((a) => (
                <TableRow key={a.gl_account_id}>
                  <TableCell>
                    {a.account_number} {a.account_name}
                  </TableCell>
                  <TableCell>
                    {canReconcile && (
                      <Button plain onClick={() => setOpening(a)}>
                        Start
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {opening && (
        <OpenReconciliationDialog
          hoaId={hoaId}
          account={opening}
          onClose={() => setOpening(null)}
          onOpened={(recId) => {
            setOpening(null);
            open(recId);
          }}
        />
      )}
    </div>
  );
};

export default ReconciliationList;
